import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  cancelReceiptRequest,
  closeReceiptRequest,
  createReceiptRequest,
  getReceiptManifestRequest,
  getReceiptRequest,
  getReceiptsRequest,
  receiveReceiptRequest,
  updateReceiptRequest,
  uploadReceiptsRequest,
} from './receiptsApi';
import { ALL_FILTER } from './receiptsConstants';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * Loads the page of receiving orders the filters describe, in the warehouse in
 * use — or in every warehouse in reach when none is picked.
 *
 * @returns {Promise<{count: number, results: Array<object>, can_confirm: boolean,
 *   status_counts: object}>}
 */
export const fetchReceipts = createAsyncThunk(
  'receipts/fetchReceipts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { status, company, source, search, page, rowsPerPage } = state.receipts.filters;

      const params = {
        page,
        page_size: rowsPerPage,
      };
      const warehouseId = selectCurrentWarehouseId(state);
      if (warehouseId) params.warehouse = warehouseId;
      if (company !== ALL_FILTER) params.company = company;
      if (status !== ALL_FILTER) params.status = status;
      if (source !== ALL_FILTER) params.source = source;
      if (search) params.search = search;

      const response = await getReceiptsRequest(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching receipts:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las recepciones'));
    }
  }
);

/**
 * One order, with its lines and its history — and whether this session may
 * confirm in its warehouse.
 *
 * `can_confirm` only travels with the listing, and it is answered per
 * warehouse: a company confirms in the one it holds and not in the centre it
 * only has a contract in. So the listing is asked for that warehouse, one row,
 * rather than working it out here.
 *
 * @param {number|string} receiptId
 * @returns {Promise<{receipt: object, canConfirm: boolean}>}
 */
export const fetchReceipt = createAsyncThunk(
  'receipts/fetchReceipt',
  async (receiptId, { rejectWithValue }) => {
    try {
      const { data: receipt } = await getReceiptRequest(receiptId);
      const { data: listing } = await getReceiptsRequest({
        warehouse: receipt.warehouse,
        page_size: 1,
      });
      return { receipt, canConfirm: Boolean(listing?.can_confirm) };
    } catch (error) {
      console.error('Error fetching receipt:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener la recepción'));
    }
  }
);

/*
 * Every write answers the whole order, which the slice keeps as the detail, and
 * reloads the listing: its counters and its totals are the server's.
 *
 * Their errors are handed over as the server wrote them. It explains well what
 * it refused and why — "controla lote; indica el lote al recibir", "ya recibió
 * con el lote X" — and rewording that here would only make it worse.
 */

/** @param {object} payload - See `buildDeclarePayload`. */
export const createReceipt = createAsyncThunk(
  'receipts/createReceipt',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await createReceiptRequest(payload);
      dispatch(fetchReceipts());
      return response.data;
    } catch (error) {
      console.error('Error creating receipt:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo crear la orden de recepción'));
    }
  }
);

/** @param {{receiptId: number, payload: object}} params - See `buildDeclareUpdatePayload`. */
export const updateReceipt = createAsyncThunk(
  'receipts/updateReceipt',
  async ({ receiptId, payload }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateReceiptRequest(receiptId, payload);
      dispatch(fetchReceipts());
      return response.data;
    } catch (error) {
      console.error('Error updating receipt:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo guardar la orden de recepción'));
    }
  }
);

/**
 * Confirms a count. This is what writes to the kardex.
 *
 * @param {{receiptId: number, payload: object}} params - See `buildReceivePayload`.
 * @returns {Promise<{receipt: object, capacityWarnings: string[]}>} The
 *   warnings are locations left over their capacity: the system says so and
 *   does not block, because the goods are physically there.
 */
export const receiveReceipt = createAsyncThunk(
  'receipts/receiveReceipt',
  async ({ receiptId, payload }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await receiveReceiptRequest(receiptId, payload);
      dispatch(fetchReceipts());
      return { receipt: data.receipt, capacityWarnings: data.capacity_warnings || [] };
    } catch (error) {
      console.error('Error receiving receipt:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo confirmar la recepción'));
    }
  }
);

/** @param {number} receiptId */
export const closeReceipt = createAsyncThunk(
  'receipts/closeReceipt',
  async (receiptId, { dispatch, rejectWithValue }) => {
    try {
      const response = await closeReceiptRequest(receiptId);
      dispatch(fetchReceipts());
      return response.data;
    } catch (error) {
      console.error('Error closing receipt:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo cerrar la recepción'));
    }
  }
);

/** @param {number} receiptId */
export const cancelReceipt = createAsyncThunk(
  'receipts/cancelReceipt',
  async (receiptId, { dispatch, rejectWithValue }) => {
    try {
      const response = await cancelReceiptRequest(receiptId);
      dispatch(fetchReceipts());
      return response.data;
    } catch (error) {
      console.error('Error cancelling receipt:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo anular la recepción'));
    }
  }
);

/**
 * Several orders out of one sheet. Resolves with the server's own summary —
 * "2 orden(es) de recepción creadas con 3 línea(s) para Ñandú SpA".
 *
 * @param {{warehouseId: number, companyId: number, file: File}} params
 * @returns {Promise<{message: string, created: number, lines: number}>}
 */
export const uploadReceipts = createAsyncThunk(
  'receipts/uploadReceipts',
  async ({ warehouseId, companyId, file }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('warehouse_id', warehouseId);
      formData.append('company_id', companyId);
      formData.append('excel_file', file);

      const response = await uploadReceiptsRequest(formData);
      dispatch(fetchReceipts());
      return response.data;
    } catch (error) {
      console.error('Error uploading receipts:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo cargar la planilla'));
    }
  }
);

/**
 * The printable manifest of an order, as an address the browser can open.
 *
 * Asked through the API client rather than opened as a link, so it travels
 * with the same session as every other call.
 *
 * @param {number} receiptId
 * @returns {Promise<string>} A blob URL.
 */
export const fetchReceiptManifest = createAsyncThunk(
  'receipts/fetchReceiptManifest',
  async (receiptId, { rejectWithValue }) => {
    try {
      const response = await getReceiptManifestRequest(receiptId);
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Error fetching receipt manifest:', error);
      // The body of a failed blob request is a blob too: there is no sentence
      // to read out of it.
      return rejectWithValue('No se pudo generar el manifiesto');
    }
  }
);
